import { useQueryClient } from "@tanstack/react-query";
import { use, useEffect, useMemo, useRef, useState } from "react";
import { ReactComponent as ArrowturnLeftIcon } from "../../assets/img/portal/return.svg";
import { ReactComponent as CloseIcon } from "assets/img/icons/header/close.svg";
import { Button, DeleteDiscussionModal, Separator } from "components/elements";
import { DiscussionAttachmentsModal } from "components/elements/DiscussionAttachmentsModal";
import { ThreadCommentForm } from "components/form/ThreadCommentForm";
import { DiscussionComments } from "components/layout/discussion/DiscussionComments";
import { useUserRoleInfo } from "@compass/shared/hooks";
import { useCurrentUser } from "hooks/useCurrentUser";
import { trpc } from "lib/trpc";
import { ClientDiscussionListEntry } from "@compass/shared/types";
import { ManageParticipantsModal } from "./ManageParticipantsModal";

export const DiscussionView = ({
    discussion,
    onClose,
}: {
    discussion: ClientDiscussionListEntry;
    onClose: () => void;
}) => {
    const [isReplyOpen, setIsReplyOpen] = useState(false);
    const [isAttachmentsOpen, setIsAttachmentsOpen] = useState(false);
    const [isManageParticipantsOpen, setIsManageParticipantsOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const replyFormRef = useRef<HTMLDivElement>(null);

    const currentUser = useCurrentUser();
    const userRoleInfo = useUserRoleInfo(currentUser);
    // Mirrors `DiscussionService.canEditParticipants` on the server (PRTL-1197).
    const canManageParticipants = userRoleInfo.canAddUsers;

    // First non-Compass org on the discussion — fills {{organization_name}} in
    // templates picked from the reply editor. `discussion.organizations` is
    // already de-duped per-org by the server formatter.
    const templateOrganizationName = useMemo(() => {
        const orgs = discussion.organizations ?? [];
        const nonCompass = orgs.find((o) => o.name.trim().toLowerCase() !== "compass");
        return nonCompass?.name;
    }, [discussion.organizations]);

    const canEditTitle = discussion.userCanEdit;
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [newTitle, setNewTitle] = useState(discussion.title);

    const queryClient = trpc.useUtils();
    const replyMutation = trpc.threads.createComment.useMutation();
    const deleteDiscussion = trpc.discussions.delete.useMutation({
        onSuccess: () => {
            void queryClient.discussions.list.invalidate();
            onClose();
        },
    });
    const updateTitle = trpc.discussions.updateTitle.useMutation({
        onSuccess: () => {
            queryClient.discussions.getDiscussionView.invalidate({ discussionId: discussion.id });
            queryClient.discussions.list.invalidate();
            setIsEditingTitle(false);
        },
    });
    const { data: compositeData } = trpc.discussions.getDiscussionView.useQuery(
        { discussionId: discussion.id },
        {
            placeholderData: (prev) => prev,
        },
    );
    const discussionDetails = compositeData?.discussion;
    const thread = compositeData?.thread;
    const threadComments = compositeData?.comments ?? [];

    useEffect(() => {
        if (compositeData) {
            queryClient.discussions.list.invalidate();
        }
    }, [compositeData, queryClient.discussions.list]);

    // Server aggregates every attachment across every comment (top-level + replies);
    // `comments` is paginated to top-level only, so client-side aggregation would
    // miss attachments on replies.
    const allAttachments = compositeData?.allAttachments ?? [];

    const handleReplyClick = () => {
        setIsReplyOpen(true);
        // scroll to bottom after click
        setTimeout(() => {
            replyFormRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
    };

    return (
        <>
            <div className="relative flex flex-col gap-4 overflow-y-auto rounded-2xl border border-[#c8c8c8] bg-white px-6">
                {/* Header Section */}
                <div className="flex items-center justify-between pt-4">
                    <div className="flex items-center gap-2">
                        {isEditingTitle ? (
                            <input
                                type="text"
                                value={newTitle}
                                onChange={(e) => setNewTitle(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        updateTitle.mutate({
                                            discussionId: discussion.id,
                                            title: newTitle,
                                        });
                                    }
                                    if (e.key === "Escape") setIsEditingTitle(false);
                                }}
                                className="rounded-md border px-2 text-xl font-medium text-gray-900 focus:ring-2 focus:ring-blue-400 focus:outline-hidden"
                                autoFocus
                            />
                        ) : (
                            <h2 className="text-left align-middle font-sans text-[24px] leading-[100%] font-bold tracking-normal text-[#424242]">
                                {discussionDetails?.title || discussion.title}
                                {discussionDetails?.isTitleEdited && (
                                    <span className="ml-2 text-sm text-stone-400 italic">
                                        (Edited)
                                    </span>
                                )}
                            </h2>
                        )}

                        {canEditTitle &&
                            (isEditingTitle ? (
                                <>
                                    <button
                                        onClick={() =>
                                            updateTitle.mutate({
                                                discussionId: discussion.id,
                                                title: newTitle,
                                            })
                                        }
                                        disabled={
                                            !newTitle.trim() ||
                                            newTitle.trim() === discussion.title ||
                                            updateTitle.isPending
                                        }
                                        className={`rounded-md px-2 py-1 text-sm text-white transition ${
                                            !newTitle.trim() ||
                                            newTitle.trim() === discussion.title ||
                                            updateTitle.isPending
                                                ? "cursor-not-allowed bg-gray-300"
                                                : "bg-blue-500 hover:bg-blue-600"
                                        }`}
                                    >
                                        Save
                                    </button>
                                    <button
                                        onClick={() => setIsEditingTitle(false)}
                                        className="rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-700 hover:bg-gray-100"
                                    >
                                        Cancel
                                    </button>
                                </>
                            ) : (
                                <button
                                    onClick={() => setIsEditingTitle(true)}
                                    className="rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-700 hover:bg-gray-100"
                                >
                                    Edit
                                </button>
                            ))}
                    </div>

                    <div className="flex items-center gap-2">
                        {canManageParticipants && (
                            <button
                                onClick={() => setIsManageParticipantsOpen(true)}
                                className="rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-700 hover:bg-gray-100"
                            >
                                Manage recipients
                            </button>
                        )}
                        {currentUser?.isCompassUser && (
                            <button
                                onClick={() => setIsDeleteModalOpen(true)}
                                disabled={deleteDiscussion.isPending}
                                className="rounded-md border border-red-200 px-2 py-1 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
                            >
                                Delete
                            </button>
                        )}
                        <button onClick={onClose} className="">
                            <CloseIcon className="h-[30px] w-[30px]" />
                        </button>
                    </div>
                </div>

                {discussion.organizations?.length > 0 && (
                    <div className="-mt-2 flex flex-wrap gap-2">
                        {discussion.organizations.map((org) => (
                            <div key={org.id} className="group relative">
                                {/* Organization badge */}
                                <span className="cursor-pointer rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700 transition-colors duration-200 group-hover:bg-gray-200">
                                    {org.name}
                                </span>

                                {org.users.length > 0 && (
                                    <div className="absolute top-full left-0 z-20 mt-1 hidden w-60 rounded-lg border border-gray-200 bg-white p-3 shadow-lg group-hover:block">
                                        <div className="flex flex-col gap-2">
                                            {org.users.map((user, i) => (
                                                <div
                                                    key={
                                                        user.type === "identified"
                                                            ? user.id
                                                            : `deleted-${i}`
                                                    }
                                                    className="flex items-center gap-2"
                                                >
                                                    {user.avatarUrl ? (
                                                        <img
                                                            src={user.avatarUrl}
                                                            alt={user.name}
                                                            className="h-6 w-6 rounded-full object-cover"
                                                        />
                                                    ) : (
                                                        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-300 text-xs font-semibold text-white">
                                                            {user.name?.[0]}
                                                        </div>
                                                    )}
                                                    <span className="text-sm text-gray-800">
                                                        {user.name}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                <Separator className="mx-[-24px] h-[0.5px] bg-[#c8c8c8]" />

                {/* Comments Section */}
                <div className="grow">
                    {(compositeData?.timeline?.length ?? threadComments.length) > 0 && (
                        <DiscussionComments
                            comments={threadComments}
                            timeline={compositeData?.timeline}
                            openingPostCommentId={compositeData?.openingPostCommentId}
                            discussionId={discussion.id}
                            threadId={thread?.id}
                            className="mt-2"
                        />
                    )}
                </div>

                {/* Reply Button & Inline Reply Form */}
                <div className="-mt-5 pb-5">
                    <div className="flex justify-end gap-2">
                        {discussion.attachmentsCount > 0 && (
                            <button
                                onClick={() => setIsAttachmentsOpen(true)}
                                className="flex items-center gap-2 rounded-lg bg-gray-100 px-4 py-2 text-gray-700 hover:bg-gray-200"
                            >
                                <span>View all attachments</span>
                            </button>
                        )}
                        {!isReplyOpen ? (
                            <button
                                onClick={handleReplyClick}
                                disabled={!thread}
                                title={!thread ? "Loading discussion…" : undefined}
                                className="flex items-center gap-2 rounded-lg bg-gray-100 px-4 py-2 text-gray-700 hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                <ArrowturnLeftIcon className="h-4 w-4" />
                                Reply
                            </button>
                        ) : null}
                    </div>

                    {isReplyOpen && !thread && (
                        <div ref={replyFormRef} className="mt-4 text-sm text-gray-500">
                            Loading reply form…
                        </div>
                    )}

                    {isReplyOpen && thread && (
                        <div ref={replyFormRef} className="mt-4">
                            <ThreadCommentForm
                                context="discussion"
                                onSubmit={({ contentJson, attachmentIds }) => {
                                    replyMutation.mutate(
                                        {
                                            threadId: thread.id,
                                            contentJson,
                                            attachmentIds,
                                        },
                                        {
                                            onSuccess: () => {
                                                queryClient.discussions.getDiscussionView.invalidate(
                                                    { discussionId: discussion.id },
                                                );
                                                queryClient.discussions.list.invalidate();
                                                setIsReplyOpen(false);
                                            },
                                        },
                                    );
                                }}
                                onCancel={() => setIsReplyOpen(false)}
                                showCancel
                                isLoading={replyMutation.isPending}
                                submitLabel="Send Message"
                                placeholder="Reply to this discussion"
                                templateOrganizationName={templateOrganizationName}
                            />
                        </div>
                    )}
                </div>
            </div>
            <DiscussionAttachmentsModal
                isOpen={isAttachmentsOpen}
                onClose={() => setIsAttachmentsOpen(false)}
                attachments={allAttachments || []}
            />
            <DeleteDiscussionModal
                isOpen={isDeleteModalOpen}
                setIsOpen={setIsDeleteModalOpen}
                onDelete={() => deleteDiscussion.mutate({ discussionId: discussion.id })}
            />
            {canManageParticipants && (
                <ManageParticipantsModal
                    isOpen={isManageParticipantsOpen}
                    onClose={() => setIsManageParticipantsOpen(false)}
                    discussionId={discussion.id}
                    // A deleted participant has no profile to manage; only identified ones list.
                    currentParticipants={(discussionDetails?.users ?? []).flatMap((u) =>
                        u.type === "identified"
                            ? [{ id: u.id, name: u.name, avatarUrl: u.avatarUrl }]
                            : [],
                    )}
                />
            )}
        </>
    );
};
